import { Icon } from '@iconify/react';
import { Box, Button, Divider, Stack, Typography, useAutocomplete } from '@mui/material';
import { autocompleteClasses, createFilterOptions } from '@mui/material/Autocomplete';
import { alpha, styled } from '@mui/material/styles';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import useThrottle from '../../hooks/useThrottle';
import Chip from '../../components/Chip/Chip';
import Link from '../../components/Link/Link';
import { getVehicleSearch } from '../../services/vehicles';
import { Vehicle } from '../../types';
import { PATHS } from '../../utils/constants';

const filterOptions = createFilterOptions<Vehicle>({
  matchFrom: 'any',
  stringify: (option) =>
    (option.VIN + option.reservationId + option.trim + option.customerName + option.orderId).toString(),
});

const Listbox = styled('ul')(({ theme }) => ({
  margin: 0,
  padding: 10,
  zIndex: 1,
  position: 'absolute',
  listStyle: 'none',
  backgroundColor: theme.palette.background.paper,
  overflow: 'auto',
  maxHeight: 800,
  color: theme.palette.common.black,
  marginLeft: 30,
  width: '50vw',
  height: '80vh',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  [theme.breakpoints.up('sm')]: {
    width: '420px',
  },
  [`& li`]: {
    padding: '10px',
    borderRadius: theme.shape.borderRadius,
  },
  [`& li.${autocompleteClasses.focused}`]: {
    backgroundColor: theme.palette.grey[200],
    cursor: 'pointer',
  },
  '& li:active': {
    backgroundColor: theme.palette.grey[300],
  },
}));

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  height: 38,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.55),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.65),
  },
  marginRight: theme.spacing(2),
  marginLeft: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 1.5),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  right: 0,
  top: 0,
}));

const StyledInputBase = styled('input')(({ theme }) => ({
  backgroundColor: 'transparent',
  outline: 'none',
  border: 'none',
  padding: '12px',
  paddingRight: theme.spacing(5),
  color: theme.palette.common.white,
  width: '50px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
}));

const matchOptions = {
  insideWords: true,
  findAllOccurrences: true,
  requireMatchAll: true,
};

const SearchBar = ({ containerStyle, searchStyle, searchInputStyle, searchListBox, searchIconStyle }: any) => {
  // use hooks
  const id = React.useId();
  const { t } = useTranslation();
  const isMounted = useRef(false);
  const checkUserTyping = useRef<any>(null);
  const scroll = useRef<any>(null);

  // local states
  const [options, setOptions] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [scrollHeight, setScrollHeight] = useState<number>(0);

  const {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    inputValue,
    popupOpen,
    focused,
  } = useAutocomplete<Vehicle>({
    id,
    options,
    getOptionLabel: (option) => option?.VIN || option.reservationId,
    isOptionEqualToValue: (option, value) => value?._id === option?._id,
    filterOptions,
  });
  // const throttledInputValue = useThrottle(inputValue, 1000);

  const fetchNextRecords = (nextPage: number) => {
    setPage(nextPage);
    getVehicleSearch(inputValue, nextPage).then((data: any) => {
      setOptions((prev: any) => [...prev, ...data.data]);
      setTotalPages(data?.totalPage);
      setScrollHeight((prev) => {
        const newHeight = scroll.current.scrollHeight;
        if (prev !== null) scroll.current.scrollBottom = newHeight - prev;
        return newHeight;
      });
    });
  };

  // lifecycle hook
  useEffect(() => {
    if (isMounted.current) {
      if (inputValue?.length >= 3) {
        if (checkUserTyping.current) {
          clearTimeout(checkUserTyping.current);
          checkUserTyping.current = null;
        }
        setLoading(true);
        setPage(1);
        checkUserTyping.current = setTimeout(() => {
          getVehicleSearch(inputValue.trim(), 1).then((data: any) => {
            setOptions(data.data);
            setTotalPages(data?.totalPage);
            setLoading(false);
            if (scroll?.current) {
              const sHeight = scroll.current.scrollHeight;
              setScrollHeight(sHeight);
            }
          });
        }, 800);
      }
      if (!inputValue?.length) setOptions([]);
    } else isMounted.current = true;
  }, [inputValue]);

  return (
    <div>
      <div style={containerStyle} {...getRootProps()}>
        <Search style={searchStyle}>
          <StyledInputBase placeholder={`${t('header_search')}`} {...getInputProps()} style={searchInputStyle} />
          <SearchIconWrapper>
            {focused ? (
              <Icon icon="clarity:window-close-line" color="gray" />
            ) : (
              <Icon icon="bytesize:search" color="gray" />
            )}
          </SearchIconWrapper>
        </Search>
      </div>
      {inputValue?.length > 0 && inputValue?.length <= 2 && (
        <Listbox
          style={searchListBox}
          {...getListboxProps()}
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
        >
          <Box>
            <Typography variant="h5" component="div" fontSize={16} color="gray" fontWeight={400} mb={1} mt={1.7}>
              Type at least 3 characters, Search by VIN, Reservation ID, Order ID, Customer Name …
            </Typography>
          </Box>
        </Listbox>
      )}
      {loading && popupOpen && inputValue?.length > 2 && (
        <Listbox
          style={searchListBox}
          {...getListboxProps()}
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
        >
          <Box>
            <Typography variant="h5" component="div" fontSize={16} fontWeight={500} mb={1} mt={1.7}>
              Loading...
            </Typography>
          </Box>
        </Listbox>
      )}
      {!loading &&
        inputValue?.length > 2 &&
        (options.length > 0 ? (
          <Listbox {...getListboxProps()} style={searchListBox} ref={scroll}>
            {(options as Vehicle[]).map((option, index) => {
              const VIN = option.VIN || option.reservationId;
              const matches = match(VIN || '', inputValue, matchOptions);
              const parts = parse(VIN || '', matches);

              const secondaryText = `${option.trim} ${option.customerName ? `by ${option.customerName}` : ''}`;
              const secondaryMatches = match(secondaryText, inputValue, matchOptions);
              const secondaryParts = parse(secondaryText, secondaryMatches);

              return (
                <Fragment key={option._id}>
                  <Link to={PATHS.vahicle(option.reservationId)} sx={{ color: (theme) => theme.palette.common.black }}>
                    <li {...getOptionProps({ option, index })}>
                      <Stack direction="row">
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 400 }}>
                            {parts.map((part, i) => {
                              const key = `${i}-${part}`;
                              return (
                                <span
                                  key={key}
                                  style={{
                                    fontWeight: part.highlight ? 700 : 400,
                                  }}
                                >
                                  {part.text}
                                </span>
                              );
                            })}
                          </Typography>
                          <Typography sx={{ fontSize: 10 }}>
                            {secondaryParts.map((part, i) => {
                              const key = `${i}-${part}`;
                              return (
                                <span
                                  key={key}
                                  style={{
                                    fontWeight: part.highlight ? 700 : 400,
                                  }}
                                >
                                  {part.text}
                                </span>
                              );
                            })}
                          </Typography>
                        </Box>
                        {option?.customerStatus && (
                          <div>
                            <Chip text={option?.customerStatus} />
                          </div>
                        )}
                      </Stack>
                    </li>
                  </Link>
                  <Divider />
                </Fragment>
              );
            })}
            {page < totalPages ? (
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  sx={{
                    mt: 2,
                    border: 'none',
                    width: '180px',
                    fontSize: '14px',
                    fontWeight: '300',
                    textTransform: 'capitalize',
                    '&:hover': { color: '#456BD9', border: 'none', background: 'white' },
                  }}
                  onClick={() => fetchNextRecords(page + 1)}
                >
                  View more
                </Button>
              </Box>
            ) : null}
          </Listbox>
        ) : (
          popupOpen && (
            <Listbox
              style={searchListBox}
              {...getListboxProps()}
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
            >
              <Box sx={{ padding: '20px 0' }}>
                <Icon icon="bytesize:search" color="#c5c5c5" fontSize={30} />
                <Typography variant="h5" component="div" fontSize={16} fontWeight={500} mb={1} mt={1.7}>
                  No matching search results
                </Typography>
                <Typography color="#a1a1a1" fontSize={14} fontWeight="400">
                  Try again using more general search terms
                </Typography>
              </Box>
            </Listbox>
          )
        ))}
    </div>
  );
};

export default SearchBar;
